import axios from "axios";
import webStorage from "@/common/module/webStorage";
import store from "@/store";
import { RouteLocationNormalized, NavigationGuardNext } from "vue-router";
import { HttpAxios } from "@/common/services/httpAxios";
import userGateway from "@/common/services/userGateway";
import { IPlatforms } from "@/common/services/userGateway/interfaceUserGateway";
import Campaign from "@/common/services/campaigns";
import Insights from "@/common/services/insights";
import jwt from "jwt-decode";
import CryptoToken from "@/common/module/cryptoToken";
import mixpanel from "mixpanel-browser";

interface IToken {
  access_token: string;
  expires_in: number;
  id_token: string;
  "not-before-policy": number;
  refresh_token: string;
  refresh_expires_in: number;
  scope: string;
  session_state: string;
  token_type: string;
}

interface IAccount {
  account: string;
  name: string;
  code: {
    allin: string;
    btg: string;
    customerId: string;
    masterId: string;
  };
  applications: { name: string; redirect: string }[];
}

class KeyCloak {
  logged: boolean | string = false;
  errorCode = "";
  private accountSelected!: IAccount;
  private featureFlags!: string[];
  private plataforms!: IPlatforms;
  private idSetTimeoutRefresh = -1;

  async verifyAuth(): Promise<boolean | string> {
    try {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const code = urlParams.get("code");

      if (
        code &&
        localStorage.getItem("code_verifier") &&
        localStorage.getItem("code_challenge")
      ) {
        this.logged = true;
        localStorage.removeItem("ACCOUNT");
        await this.exchangeCodeForTokens(code);
        this.expAccessToken();
      } else if (webStorage.getItem("access_token")) {
        this.logged = true;
        store.dispatch("token/SET_TOKEN", webStorage.getItem("refresh_token"));
        this.expAccessToken();
      }

      if (this.logged) {
        await this.prepareAccount();
      }

      return this.logged;
    } catch (error: any) {
      this.errorCode =
        error?.response?.data[0]?.propertyName ??
        error?.response?.data?.error_description;
      this.logged = "error";
      console.error("ERRO AO PREPARAR CONTAS:", error);
      return this.logged;
    }
  }

  async exchangeCodeForTokens(code: string, pathUri = "") {
    const response = await axios.post<IToken>(
      `${process.env.VUE_APP_KEY_CLOAK_URL}/token`,
      {
        grant_type: "authorization_code",
        code: code,
        client_id: process.env.VUE_APP_CLIENT_ID,
        code_verifier: localStorage.getItem("code_verifier"),
        redirect_uri: process.env.VUE_APP_URL + pathUri,
      },
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    webStorage.setItem("access_token", response.data.access_token);
    webStorage.setItem("id_token", response.data.id_token);
    webStorage.setItem("refresh_token", response.data.refresh_token);
    store.dispatch("token/SET_TOKEN", response.data.refresh_token);
  }

  async prepareAccount() {
    await HttpAxios.initWithKeyCloak(webStorage.getItem("access_token"));
    const accounts = await userGateway.getAccounts();
    const accountParse = accounts.map((account): IAccount => {
      return {
        name: account.accountName,
        account: account.dxpAccountId,
        code: { allin: "", btg: "", customerId: "", masterId: "" },
        applications: [],
      };
    });
    store.dispatch("user/SET_ACCOUNTS", accountParse);
    this.accountSelected = JSON.parse(localStorage.getItem("ACCOUNT") ?? "{}");

    if (!this.accountSelected.name) {
      this.accountSelected = accountParse[0];
    }

    const setUp = await userGateway.getSetUp(this.accountSelected.account);
    this.plataforms = setUp.plataforms;
    this.featureFlags = setUp.featureFlags;

    await HttpAxios.initWithKeyCloak(
      webStorage.getItem("access_token"),
      this.accountSelected.account
    );

    this.preparePlatform();
    localStorage.setItem("ACCOUNT", JSON.stringify(this.accountSelected));
    this.prepareFeatureFlag();
    await this.preparePermissionsPage();
    this.prepareUSerInfo();
  }

  private preparePlatform() {
    const application = {
      allinId: {
        name: "All in",
        redirect:
          process.env.VUE_APP_ALLIN_PLATFORM +
          "/jumper_auth.php?xpAccountId=${accountId}",
      },
      btgId: {
        name: "BTG",
        redirect:
          process.env.VUE_APP_BTG_PLATFORM +
          "/cas-login/${casToken}/${accountId}",
      },
      customerId: {
        name: "Social miner",
        redirect:
          process.env.VUE_APP_SOCIALMINER_PLATFORM +
          "?xpAccountId=${accountId}",
      },
    };
    this.accountSelected.applications = [];

    for (const app of Object.keys(this.plataforms)) {
      if (this.plataforms[app] && app !== "masterId") {
        this.accountSelected.applications.push(application[app]);
      }
    }

    this.accountSelected.code.allin = this.plataforms.allinId ?? "";
    this.accountSelected.code.btg = this.plataforms.btgId ?? "";
    this.accountSelected.code.customerId = this.plataforms.customerId ?? "";
    this.accountSelected.code.masterId = this.plataforms.masterId ?? "";
  }

  private prepareFeatureFlag() {
    const permissionsObj = {};

    this.featureFlags.forEach((featureFlag) => {
      permissionsObj[featureFlag] = true;
    });

    permissionsObj["ENV_PROD"] = process.env.NODE_ENV === "production";
    permissionsObj["ENV_NOT_PROD"] = process.env.NODE_ENV !== "production";
    permissionsObj["ENV_HOMOL"] = process.env.NODE_ENV === "homol";
    permissionsObj["ENV_DEV"] = process.env.NODE_ENV === "development";
    permissionsObj["ENV_WAKE_STORE"] =
      this.accountSelected.code.customerId ===
      "b3c064cc-353a-4b89-918f-efc17a51882e";
    permissionsObj["ENV_MARISA"] =
      this.accountSelected.code.customerId ===
      "45d93d22-f61c-42c2-9ed1-1946d160a45a";

    permissionsObj["ENV_HUGO_BOSS"] =
      this.accountSelected.code.customerId ===
      "66b7e657-64c2-478b-93b4-7c67c41c70ed";

    permissionsObj["ENV_MASTERTAPANY"] =
      this.accountSelected.code.customerId ===
      "95bee6a0-7923-42ef-b30b-8ca6691285f8";

    store.dispatch("permissions/SET_PERMISSIONS_OBJ", permissionsObj);
  }

  featureFlag(value: string): boolean {
    const permissionsObj = store.getters["permissions/permissionsObj"];
    return permissionsObj[value] || false;
  }

  private async preparePermissionsPage() {
    const setWhatsappCampaign = <T>(result: PromiseSettledResult<T>) => {
      if (result.status === "fulfilled") {
        store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
          pageName: "whatsapp_campaign",
          value: !!result.value,
        });
        store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
          pageName: "whatsapp_campaign_phoneId",
          value: result.value,
        });
        return;
      }

      console.log("Permission page: whatsapp_campaign.\n", result.reason);
    };

    const setSmsAndAppPush = <T>(result: PromiseSettledResult<any>) => {
      if (result.status === "fulfilled") {
        store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
          pageName: "sms_campaign",
          value: !!result.value.smsActive,
        });

        store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
          pageName: "app_push_campaign",
          value: !!result.value.pushActive,
        });
        return;
      }

      console.log("Permission page: sms_campaign.\n", result.reason);
    };

    const setGA = <T>(result: PromiseSettledResult<T>) => {
      if (result.status === "fulfilled") {
        store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
          pageName: "insights",
          value: result.value,
        });
        return;
      }

      console.log("Permission page: insights.\n", result.reason);
    };
    const setProductsInNewPricing = <T>(result: PromiseSettledResult<any>) => {
      if (result.status === "fulfilled") {
        store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
          pageName: "historical-consumption",
          value: result.value.isNewPricing,
        });

        result.value.products.forEach((value) => {
          store.dispatch(`permissions/SET_PERMISSIONS_PAGE`, {
            pageName: value,
            value: true,
          });
        });
        return;
      }

      console.log("Permission page: insights.\n", result.reason);
    };
    const runCallback: [
      (result: Parameters<typeof setWhatsappCampaign>[0]) => void,
      (result: Parameters<typeof setSmsAndAppPush>[0]) => void,
      (result: Parameters<typeof setGA>[0]) => void,
      (result: Parameters<typeof setProductsInNewPricing>[0]) => void
    ] = [setWhatsappCampaign, setSmsAndAppPush, setGA, setProductsInNewPricing];

    await Promise.allSettled([
      Campaign.hasWhatsappCampaign(),
      Campaign.getAdvancedConfigs(),
      Insights.getUserHasGA(),
      Insights.getProductsInNewPricing(),
    ]).then((results) => {
      results.forEach((result, i) => {
        runCallback[i](result);
      });
    });
  }

  permissionPage<T = boolean>(page: string): T {
    const permissionsPage = store.getters["permissions/permissionsPage"];
    return permissionsPage[page] || false;
  }

  private prepareUSerInfo() {
    const userInfo: { sub: string; email: string } = jwt(
      webStorage.getItem("access_token")
    );
    const userData = {
      _id: userInfo.sub,
      email: userInfo.email,
      name: userInfo.email,
    };

    store.dispatch("user/SET_USER", userData);

    mixpanel.identify(userInfo.email);
    mixpanel.people.set(userData);

    mixpanel.register({
      Master_id: this.accountSelected.code.masterId,
      Account_id: this.accountSelected.account,
      Email: userInfo.email,
    });
  }

  redirectAuth() {
    try {
      localStorage.getItem("id_token") ? this.logout() : this.redirectToLogin();
    } catch (error) {
      console.error("ERRO AO EXECUTAR redirectAuth", error);
      this.redirectToLogin();
    }
  }

  redirectToLogin() {
    this.clearTokens();
    CryptoToken.generatePKCE();

    const url = new URL(`${process.env.VUE_APP_KEY_CLOAK_URL}/auth`);
    url.searchParams.set("client_id", process.env.VUE_APP_CLIENT_ID!);
    url.searchParams.set("redirect_uri", process.env.VUE_APP_URL!);
    url.searchParams.set("scope", process.env.VUE_APP_SCOPE!);
    url.searchParams.set("response_type", process.env.VUE_APP_RESPONSE_TYPE!);
    url.searchParams.set(
      "code_challenge",
      localStorage.getItem("code_challenge") as string
    );
    url.searchParams.set("code_challenge_method", "S256");

    window.location.href = url.href;
  }

  urlGetNewCode() {
    CryptoToken.generatePKCE();

    const url = new URL(`${process.env.VUE_APP_KEY_CLOAK_URL}/auth`);
    url.searchParams.set("client_id", process.env.VUE_APP_CLIENT_ID!);
    url.searchParams.set(
      "redirect_uri",
      process.env.VUE_APP_URL! + process.env.VUE_APP_REFRESH_PAGE!
    );
    url.searchParams.set("scope", process.env.VUE_APP_SCOPE!);
    url.searchParams.set("response_type", process.env.VUE_APP_RESPONSE_TYPE!);
    url.searchParams.set(
      "code_challenge",
      localStorage.getItem("code_challenge") as string
    );
    url.searchParams.set("code_challenge_method", "S256");

    return url.href;
  }

  logout() {
    const url = new URL(`${process.env.VUE_APP_KEY_CLOAK_URL}/logout`);
    url.searchParams.set("post_logout_redirect_uri", process.env.VUE_APP_URL!);
    url.searchParams.set("id_token_hint", webStorage.getItem("id_token"));

    this.clearTokens();

    window.location.href = url.href;
  }

  clearTokens() {
    webStorage.removeItem("access_token");
    webStorage.removeItem("refresh_token");
    webStorage.removeItem("id_token");
    webStorage.removeItem("cas_token");
    localStorage.removeItem("code_verifier");
    localStorage.removeItem("code_challenge");
    localStorage.removeItem("ACCOUNT");
  }

  async silentRefreshToken() {
    try {
      const refreshToken = webStorage.getItem("refresh_token");
      const response = await axios.post<IToken>(
        `${process.env.VUE_APP_KEY_CLOAK_URL}/token`,
        {
          grant_type: "refresh_token",
          client_id: process.env.VUE_APP_CLIENT_ID,
          refresh_token: refreshToken,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      webStorage.setItem("access_token", response.data.access_token);
      webStorage.setItem("id_token", response.data.id_token);
      webStorage.setItem("refresh_token", response.data.refresh_token);
      store.dispatch("token/SET_TOKEN", response.data.refresh_token);
      HttpAxios.updateAuthorizationToken(response.data.access_token);
      clearTimeout(this.idSetTimeoutRefresh);
      this.expAccessToken();
    } catch (error) {
      console.error("ERRO AO RENOVAR O TOKEN", error);
      this.silentGetNewCode();
      return null;
    }
  }

  silentGetNewCode() {
    const iframe = document.createElement("iframe");
    iframe.src = this.urlGetNewCode();
    iframe.width = "0";
    iframe.height = "0";
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.remove();
    };
  }

  expAccessToken() {
    const expToken: { exp: number } = jwt(webStorage.getItem("access_token"));
    const timeInSeconds = Date.now() / 1000;
    const diference = Math.floor(expToken.exp - timeInSeconds);
    const timeToRefreshInMilliseconds = Math.floor((diference / 2) * 1000);

    if (diference > 0) {
      this.idSetTimeoutRefresh = window.setTimeout(() => {
        this.silentRefreshToken();
      }, timeToRefreshInMilliseconds);
    }
  }

  postMessageToken() {
    window.addEventListener("message", async (event: MessageEvent) => {
      if (
        process.env.VUE_APP_EDITOR === event.origin &&
        (event.data === "refreshToken" || event.data === "getToken")
      ) {
        event.source?.postMessage(
          JSON.stringify({
            data: event.data,
            access_token: webStorage.getItem("access_token"),
            refresh_token: webStorage.getItem("refresh_token"),
            id_token: webStorage.getItem("id_token"),
          }),
          event.origin as any
        );
      }

      if (process.env.VUE_APP_ORIGIN === event.origin && event.data?.code) {
        await this.exchangeCodeForTokens(
          event.data?.code,
          process.env.VUE_APP_REFRESH_PAGE
        );
        HttpAxios.updateAuthorizationToken(webStorage.getItem("access_token"));
        clearTimeout(this.idSetTimeoutRefresh);
        this.expAccessToken();
      }
    });
  }

  hookBeforeEnterPage(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) {
    setTimeout(() => {
      window.scrollTo(0, 0);
      next();
    }, 100);
  }

  clearQueryParameters(to: RouteLocationNormalized) {
    if (
      typeof to.query.code !== "undefined" ||
      typeof to.query.app !== "undefined"
    ) {
      window.history.pushState({}, document.title, window.location.pathname);
    }
  }

  validateAccessToken() {
    const expToken: { exp: number } = jwt(webStorage.getItem("access_token"));
    const timeInSeconds = Date.now() / 1000;
    const diference = Math.floor(expToken.exp - timeInSeconds);

    return diference > 0 ? true : false;
  }
}

export default new KeyCloak();
